/** Realtime bridge (realtime-web-delivery). Holds one Centrifugo WebSocket for the
    session and, on any publication, invalidates the notification queries so the
    polled hooks re-render with fresh data — the badge/feed go live. Couchbase stays
    the source of truth (the push is a nudge, not the data), and polling remains the
    fallback, so a dropped socket degrades gracefully.

    Authorization is by Centrifugo's connect proxy: the browser hands its Keycloak
    access token in the connect `data`, the backend validates it and returns the
    user's channels, which Centrifugo server-side-subscribes — so this client never
    needs the user id and just listens for publications. */

import { Centrifuge } from "centrifuge";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { useAuth } from "./auth";
import { readRealtimeUrl } from "./config";

const NOTIFICATION_KEY = ["pencil", "notifications"] as const;

export function RealtimeBridge() {
  const { status, getAccessToken } = useAuth();
  const qc = useQueryClient();

  useEffect(() => {
    const url = readRealtimeUrl();
    if (url == null || status !== "authenticated") return;

    let disposed = false;
    let client: Centrifuge | null = null;

    void (async () => {
      const token = await getAccessToken();
      if (disposed || !token) return;
      // The connect proxy reads the token from connect `data`; Centrifugo forwards
      // it verbatim. `getData` re-reads a fresh token on every (re)connect so a
      // reconnection after the token rotated still authorizes.
      client = new Centrifuge(url, {
        getData: async () => ({ token: (await getAccessToken()) ?? "" }),
        data: { token },
      });
      // Server-side subscriptions (the channels the proxy granted) deliver here.
      client.on("publication", (ctx) => {
        void qc.invalidateQueries({ queryKey: NOTIFICATION_KEY });
        // A message push carries its conversation id: refetch that open thread and
        // the inbox so the chat updates live (the REST thread stays authoritative,
        // so a refetch — not a delta — means there are no gaps to reconcile).
        const conversationId = (ctx.data as { conversation_id?: string } | undefined)
          ?.conversation_id;
        if (conversationId) {
          void qc.invalidateQueries({
            queryKey: ["pencil", "conversations", conversationId, "messages"],
          });
          void qc.invalidateQueries({ queryKey: ["pencil", "conversations", "org"] });
        }
      });
      client.connect();
    })();

    return () => {
      disposed = true;
      client?.disconnect();
    };
  }, [status, getAccessToken, qc]);

  return null;
}
